import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Columns3 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const STATUS_C = { matched: "text-teal-dark", ambiguous: "text-yellow-600", missing: "text-red-600" };

const seedMapping = (sheets) => {
  const m = {};
  Object.entries(sheets || {}).forEach(([sk, s]) => {
    m[sk] = {};
    (s.fields || []).forEach((f) => { const v = s.mapping?.[f.key]; m[sk][f.key] = v === null || v === undefined ? "" : String(v); });
  });
  return m;
};

export const ImportWorkbook = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setFile(null); setPreview(null); setReport(null); setMapping(null); setShowMap(false); };

  const runPreview = async (mapObj) => {
    if (!file) { toast.error("Choose an .xlsx file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (mapObj) fd.append("mapping", JSON.stringify(mapObj));
      const r = await api.post("/people/import/preview", fd);
      setPreview(r.data);
      if (!mapObj) setMapping(seedMapping(r.data.sheets));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read workbook"); }
    setBusy(false);
  };

  const commit = async () => {
    setBusy(true);
    try { const r = await api.post("/people/import/commit", { students: preview.students, parents: preview.parents }); setReport(r.data); toast.success("Import complete"); onDone && onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };

  const setMapField = (sheet, field, val) => setMapping((m) => ({ ...m, [sheet]: { ...m[sheet], [field]: val } }));

  const setStu = (i, patch) => setPreview((p) => ({ ...p, students: p.students.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const setStuClass = (i, ci, cid) => setPreview((p) => ({ ...p, students: p.students.map((s, idx) => idx === i ? { ...s, classes: s.classes.map((c, k) => k === ci ? { ...c, class_id: cid, matched: !!cid } : c) } : s) }));
  const setPar = (i, patch) => setPreview((p) => ({ ...p, parents: p.parents.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const setParLink = (i, li, did) => setPreview((p) => ({ ...p, parents: p.parents.map((s, idx) => idx === i ? { ...s, students: s.students.map((l, k) => k === li ? { ...l, dancer_id: did, status: did ? "matched" : l.status } : l) } : s) }));

  return (
    <>
      <Button data-testid="import-workbook-button" variant="outline" className="rounded-full" onClick={() => { reset(); setOpen(true); }}>
        <FileSpreadsheet className="w-4 h-4 mr-2" />Import workbook
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">Import Excel workbook</DialogTitle></DialogHeader>

          {report ? (
            <div className="space-y-3" data-testid="import-report">
              <div className="flex items-center gap-2 text-teal-dark font-head font-bold"><CheckCircle2 className="w-5 h-5" />Import complete</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                {[["Dancers created", report.dancers_created], ["Dancers matched", report.dancers_matched], ["Dancers skipped", report.dancers_skipped], ["Parents created", report.parents_created], ["Parents matched", report.parents_matched], ["Parents skipped", report.parents_skipped], ["Enrollments added", report.enrollments_added], ["Parent links added", report.links_added], ["Classes skipped", report.classes_skipped], ["Students unresolved", report.students_unresolved]].map(([l, v]) => (
                  <div key={l} className="rounded-xl bg-gray-50 border p-2"><span className="font-bold">{v}</span> <span className="text-muted-foreground">{l}</span></div>
                ))}
              </div>
              {report.classes_skipped + report.students_unresolved > 0 && <p className="text-xs text-yellow-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Some classes/students were skipped (unmatched or missing) — they were left for you to resolve manually.</p>}
              <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal-dark" onClick={() => setOpen(false)}>Done</Button></DialogFooter>
            </div>
          ) : !preview ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Upload a workbook with a <strong>Students</strong> tab (First Name, Last, Phone, Classes, Gender, Age) and a <strong>Parents</strong> tab (First Name, Last Name, Students, Email). Headers don't have to match exactly — you can map columns on the next step. Nothing is saved until you review and confirm.</p>
              <input ref={fileRef} data-testid="import-file-input" type="file" accept=".xlsx" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
              <div className="flex items-center gap-2">
                <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()}><Upload className="w-4 h-4 mr-1" />Choose file</Button>
                <span className="text-sm text-muted-foreground">{file?.name || "No file selected"}</span>
              </div>
              <DialogFooter><Button data-testid="import-preview-button" disabled={busy || !file} className="rounded-full bg-teal hover:bg-teal-dark" onClick={() => runPreview(null)}>{busy ? "Reading…" : "Preview import"}</Button></DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              {preview.warnings?.length > 0 && <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-100 rounded-lg p-2">{preview.warnings.join(" · ")}</div>}

              {/* Column mapping */}
              <div className="border rounded-xl">
                <button type="button" data-testid="toggle-column-map" onClick={() => setShowMap((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-semibold">
                  <Columns3 className="w-4 h-4 text-teal" />Column mapping
                  <span className="text-xs font-normal text-muted-foreground ml-1">— headers didn't line up? Point each field to the right column.</span>
                  <span className="ml-auto text-teal-dark">{showMap ? "Hide" : "Adjust"}</span>
                </button>
                {showMap && (
                  <div className="px-3 pb-3 space-y-3" data-testid="column-map-panel">
                    {Object.entries(preview.sheets || {}).map(([sk, s]) => (
                      <div key={sk}>
                        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">{sk} tab</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {(s.fields || []).map((f) => (
                            <label key={f.key} className="text-xs">
                              <span className="block text-muted-foreground mb-0.5">{f.label}</span>
                              <select data-testid={`map-${sk}-${f.key}`} className="w-full border rounded-md h-8 px-1.5 text-xs" value={mapping?.[sk]?.[f.key] ?? ""} onChange={(e) => setMapField(sk, f.key, e.target.value)}>
                                <option value="">— (auto) —</option>
                                {(s.headers || []).map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                              </select>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                    <Button size="sm" data-testid="apply-mapping-button" disabled={busy} className="rounded-full bg-teal hover:bg-teal-dark" onClick={() => runPreview(mapping)}>{busy ? "Re-reading…" : "Apply mapping & re-read"}</Button>
                  </div>
                )}
              </div>

              <div>
                <p className="font-head font-bold mb-2">Students ({preview.students.length})</p>
                <div className="space-y-2">
                  {preview.students.map((s, i) => (
                    <div key={s.key} data-testid={`import-student-${i}`} className="border rounded-xl p-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{s.first_name} {s.last_name}</span>
                        {s.ambiguous && <span className="text-yellow-600 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" />ambiguous name</span>}
                        <select data-testid={`import-student-action-${i}`} className="border rounded-md h-8 px-2 text-xs ml-auto" value={s.action} onChange={(e) => setStu(i, { action: e.target.value })}>
                          <option value="create">Create new</option>
                          {s.candidates.length > 0 && <option value="match">Match existing</option>}
                          <option value="skip">Skip</option>
                        </select>
                        {s.action === "match" && s.candidates.length > 0 && (
                          <select data-testid={`import-student-match-${i}`} className="border rounded-md h-8 px-2 text-xs" value={s.dancer_id} onChange={(e) => setStu(i, { dancer_id: e.target.value })}>
                            {s.candidates.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        )}
                      </div>
                      {s.classes.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {s.classes.map((c, ci) => (
                            <span key={ci} className="flex items-center gap-1 text-xs">
                              <span className="text-muted-foreground">{c.raw}:</span>
                              <select data-testid={`import-student-class-${i}-${ci}`} className={`border rounded-md h-7 px-1 text-xs ${!c.class_id ? "border-red-300" : ""}`} value={c.class_id} onChange={(e) => setStuClass(i, ci, e.target.value)}>
                                <option value="">Skip (no match)</option>
                                {preview.class_options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                              </select>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-head font-bold mb-2">Parents ({preview.parents.length})</p>
                <div className="space-y-2">
                  {preview.parents.map((p, i) => (
                    <div key={p.key} data-testid={`import-parent-${i}`} className="border rounded-xl p-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{p.first_name} {p.last_name}</span>
                        <span className="text-xs text-muted-foreground">{p.email}</span>
                        <select data-testid={`import-parent-action-${i}`} className="border rounded-md h-8 px-2 text-xs ml-auto" value={p.action} onChange={(e) => setPar(i, { action: e.target.value })}>
                          <option value="create">Create new</option>
                          {p.candidates.length > 0 && <option value="match">Match existing</option>}
                          <option value="skip">Skip</option>
                        </select>
                      </div>
                      <div className="mt-2 space-y-1">
                        {p.students.map((l, li) => (
                          <div key={li} className="flex items-center gap-2 text-xs">
                            <span className="text-muted-foreground">{l.raw}</span>
                            <span className={STATUS_C[l.status] || ""}>{l.status}</span>
                            {(l.status === "ambiguous" || (l.status === "missing" && l.options.length > 0)) && (
                              <select data-testid={`import-parent-link-${i}-${li}`} className="border rounded-md h-7 px-1 text-xs" value={l.dancer_id} onChange={(e) => setParLink(i, li, e.target.value)}>
                                <option value="">Choose dancer…</option>
                                {l.options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                              </select>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-full" onClick={reset}>Back</Button>
                <Button data-testid="import-commit-button" disabled={busy} className="rounded-full bg-teal hover:bg-teal-dark" onClick={commit}>{busy ? "Importing…" : "Confirm import"}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
